import React from 'react';

import type { Dayjs } from 'dayjs';
import dayjs from '~/timeInit';

import { Alert, MenuItem, Stack, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

import type { CppPartner } from '~/types/costPerPoint';

import { type InvoiceMonth, MONTH_FORMAT, currentMonth, toMonth } from './monthRange';

interface Props {
  partners: Array<CppPartner>;
  partnerId: string;
  month: InvoiceMonth;
  onPartnerChange: (partnerId: string) => void;
  onMonthChange: (month: InvoiceMonth) => void;
  /** True when the partner directory could not be loaded. */
  hasPartnersError?: boolean;
  /** Right-aligned actions, e.g. an export or print button. */
  actions?: React.ReactNode;
}

/**
 * Partner and month selector shared by the partner Transactions and Invoice tabs.
 *
 * Both views bill and report by whole calendar month, so the period is chosen as a month rather
 * than a free date range — there is no way to ask for a partial period that could not be invoiced.
 * Future months are unselectable; the current, still-running month is allowed, and the invoice
 * surfaces the service's `PERIOD_NOT_SETTLED` blocker for it rather than hiding it here.
 */
export default function PartnerMonthFilters({
  partners,
  partnerId,
  month,
  onPartnerChange,
  onMonthChange,
  hasPartnersError = false,
  actions,
}: Props) {
  const handleMonthChange = (next: Dayjs | null) => {
    if (next?.isValid()) {
      onMonthChange(toMonth(next));
    }
  };

  return (
    <Stack spacing={2}>
      {hasPartnersError && <Alert severity="error">Could not load the partner list. Try reloading the page.</Alert>}

      {/*
        Every control is `size="small"` and carries no helper text, so the row is a single
        consistent height and centring lines the inputs up. A conditional helper text under one
        field would make it taller than its neighbours and shift the row as soon as it cleared —
        the "select a partner" prompt lives at page level instead, where it is already shown.
      */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <TextField
          select
          required
          size="small"
          label="Partner"
          value={partnerId}
          onChange={(event) => onPartnerChange(event.target.value)}
          sx={{ minWidth: 260 }}
        >
          {partners.map((partner) => (
            <MenuItem key={partner.id} value={partner.id}>
              {partner.name}
            </MenuItem>
          ))}
        </TextField>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Month"
            views={['year', 'month']}
            openTo="month"
            format="MMMM YYYY"
            value={dayjs(month, MONTH_FORMAT)}
            onChange={handleMonthChange}
            maxDate={dayjs(currentMonth(), MONTH_FORMAT)}
            slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } } }}
          />
        </LocalizationProvider>

        {actions && (
          <Stack direction="row" spacing={1} sx={{ ml: { sm: 'auto' } }}>
            {actions}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
